import { useContext, useEffect, useMemo } from "react";
import classNames from "classnames";
import { PaginatedTransactionsTableContext } from "../PaginatedTransactionsTableContextProvider";
import { ViewTable } from "../../views/ViewTable";
import { useBulkActions } from "../TransactionsBulkActions/useBulkActions";
import { NoTransactions } from "../NoTransactions";
import { TransactionView } from "../../../common/types/views/transactionViews";
import { useNonLockedList } from "../../../hooks/useNonLockedList";
import styles from "./TransactionsView.module.scss";
import { TransactionsViewFooter } from "./TransactionsViewFooter";
import { transactionsViewConfig } from "./transactionsViewConfig";

interface Props {
    view: TransactionView;
    setSelectedTransactionId?: (id: number | string | undefined) => void;
    selectedTransactionId?: number;
    useCache?: boolean;
    scrollContainerRef?: React.RefObject<HTMLElement>;
    className?: string;
    stickyFooter?: boolean;
    emptyView?: () => React.ReactNode;
    autoSelect?: boolean;
}

export const TransactionsView: React.FC<Props> = ({
    view,
    setSelectedTransactionId,
    selectedTransactionId,
    scrollContainerRef,
    className,
    stickyFooter = true,
    emptyView = () => <NoTransactions />,
    autoSelect = false,
}) => {
    const { transactions, handleSortChange, currentSort } = useContext(
        PaginatedTransactionsTableContext,
    );
    const data = useMemo(() => transactions ?? null, [transactions]);
    const bulkActions = useBulkActions();

    const nonLockedTransactions = useNonLockedList(transactions);

    const enableRowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return false;
        }
        // Disable individual row selection when "select all" is active
        if (bulkActions.hasSelectedAll) {
            return false;
        }
        return (transaction: any) =>
            // A transaction can be selected if it's in the non-locked list
            nonLockedTransactions?.some((t) => t.id === transaction.id) ??
            false;
    }, [
        view.options?.bulkActions,
        nonLockedTransactions,
        bulkActions.hasSelectedAll,
    ]);

    useEffect(() => {
        if (
            view.options?.selection &&
            autoSelect &&
            data &&
            data.length > 0 &&
            !selectedTransactionId
        ) {
            setSelectedTransactionId?.(data[0].id);
        }
    }, [
        autoSelect,
        data,
        selectedTransactionId,
        setSelectedTransactionId,
        view.options?.selection,
    ]);

    const rowSelection = useMemo(() => {
        if (!bulkActions.enabled) {
            return {};
        }

        const selection: Record<string, boolean> = {};

        if (bulkActions.hasSelectedAll) {
            nonLockedTransactions?.forEach((transaction) => {
                selection[transaction.id] = true;
            });
        } else {
            Object.keys(bulkActions.selected).forEach((id) => {
                selection[id] = true;
            });
        }

        return selection;
    }, [
        bulkActions.selected,
        bulkActions.enabled,
        bulkActions.hasSelectedAll,
        nonLockedTransactions,
    ]);

    const handleRowSelectionChange = (
        updater:
            | Record<string, boolean>
            | ((prev: Record<string, boolean>) => Record<string, boolean>),
    ) => {
        if (!bulkActions.enabled || !data) {
            return;
        }

        let nextSelection: Record<string, boolean>;
        if (typeof updater === "function") {
            nextSelection = updater(rowSelection);
        } else {
            nextSelection = updater;
        }

        const selectedIds = Object.keys(nextSelection).filter(
            (id) => nextSelection[id],
        );

        const selectedTransactions = data.filter((transaction) =>
            selectedIds.includes(String(transaction.id)),
        );

        if (selectedIds.length === 0) {
            bulkActions.clearSelection();
        } else {
            bulkActions.clearSelection();
            bulkActions.select(selectedTransactions);
        }
    };

    return (
        <div className={classNames(styles.transactionsView, className)}>
            <div className={styles.viewWrapper}>
                <ViewTable
                    data={data}
                    view={view}
                    config={transactionsViewConfig}
                    selectedRowId={selectedTransactionId}
                    onRowClick={(row) => setSelectedTransactionId?.(row.id)}
                    rowSelection={rowSelection}
                    onRowSelectionChange={handleRowSelectionChange}
                    enableRowSelection={enableRowSelection}
                    sort={currentSort}
                    onSortChange={handleSortChange}
                    scrollContainerRef={scrollContainerRef}
                    emptyView={emptyView}
                />
                <TransactionsViewFooter sticky={stickyFooter} />
            </div>
        </div>
    );
};
